/**
 * ✅ CRIT-002 FIX: XSS Protection Composable (ADR-154)
 *
 * Reusable composable for HTML sanitization using DOMPurify
 * Defense-in-Depth Security Pattern de PATTERNS_FROM_BASKET_BASE.md
 *
 * Usage:
 * ```javascript
 * import { useSanitizeHtml } from '@/composables/useSanitizeHtml'
 *
 * const { sanitizeHtml } = useSanitizeHtml()
 * const safeContent = sanitizeHtml(userContent)
 * ```
 *
 * GDPR Compliance: Article 32 - Security of Processing
 * Protection against XSS attacks in user-generated content
 */

import DOMPurify from 'dompurify'

/**
 * Default safe configuration for HTML sanitization
 * Whitelist approach: only explicitly allowed tags/attributes
 */
const DEFAULT_CONFIG = {
  // Whitelist safe HTML tags
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'strong', 'em', 'u', 'i', 'b', 's', 'strike',

    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',

    // Lists
    'ul', 'ol', 'li',

    // Links and media
    'a', 'img',

    // Quotes and code
    'blockquote', 'code', 'pre',

    // Tables
    'table', 'thead', 'tbody', 'tr', 'th', 'td',

    // Containers
    'div', 'span',

    // Other formatting
    'mark', 'sup', 'sub', 'figure', 'figcaption'
  ],

  // Whitelist safe attributes
  ALLOWED_ATTR: [
    // Links
    'href', 'target', 'rel',

    // Images
    'src', 'alt', 'title', 'width', 'height', 'loading',

    // Styling (limited)
    'class', 'id',

    // Accessibility
    'aria-label', 'aria-labelledby', 'aria-describedby'
  ],

  // Blacklist dangerous tags (defense in depth)
  FORBID_TAGS: [
    'script', 'iframe', 'object', 'embed', 'applet',
    'form', 'input', 'button', 'textarea', 'select',
    'style', 'link', 'meta', 'base'
  ],

  // Blacklist event handlers (XSS vectors)
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout',
    'onfocus', 'onblur', 'onchange', 'onsubmit', 'ondblclick',
    'onkeydown', 'onkeyup', 'onkeypress', 'onmousedown', 'onmouseup',
    'onmousemove', 'onwheel', 'ontouchstart', 'ontouchend', 'ontouchmove',
    'oncontextmenu', 'ondrag', 'ondrop', 'oninput', 'oninvalid'
  ],

  // Additional security settings
  ALLOW_DATA_ATTR: false,        // No data-* attributes (can be XSS vectors)
  SAFE_FOR_TEMPLATES: true,       // Safe for Vue templates
  RETURN_TRUSTED_TYPE: false,     // Return string (not TrustedHTML)
  WHOLE_DOCUMENT: false,          // Only sanitize fragment (not full document)
  IN_PLACE: false,                // Return new string (don't modify in place)

  // Protocol whitelist for links
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|webcal):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
}

/**
 * Configuration for blog content with more permissive settings
 * Used for admin-created blog posts where some HTML is expected
 */
const BLOG_CONTENT_CONFIG = {
  ...DEFAULT_CONFIG,
  ALLOWED_TAGS: [
    ...DEFAULT_CONFIG.ALLOWED_TAGS,
    'hr', 'small', 'abbr', 'cite', 'q', 'del', 'ins'
  ],
  // Allow inline styles for blog formatting (with sanitization)
  ALLOWED_ATTR: [
    ...DEFAULT_CONFIG.ALLOWED_ATTR,
    'style' // Note: DOMPurify sanitizes inline styles by default
  ]
}

/**
 * Strict configuration for user input (comments, feedback)
 * Minimal HTML allowed, maximum security
 */
const STRICT_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
  FORBID_TAGS: DEFAULT_CONFIG.FORBID_TAGS,
  FORBID_ATTR: DEFAULT_CONFIG.FORBID_ATTR,
  ALLOW_DATA_ATTR: false,
  SAFE_FOR_TEMPLATES: true
}

/**
 * Main composable function
 */
export function useSanitizeHtml() {
  /**
   * Sanitize HTML content with configurable options
   *
   * @param {string} html - Raw HTML to sanitize
   * @param {Object} options - Custom DOMPurify configuration
   * @param {string} mode - Preset mode: 'default', 'blog', 'strict'
   * @returns {string} Sanitized HTML safe for rendering with v-html
   */
  const sanitizeHtml = (html, options = {}, mode = 'default') => {
    // Handle empty input
    if (!html || typeof html !== 'string') {
      return ''
    }

    // Select configuration based on mode
    let config = DEFAULT_CONFIG

    switch (mode) {
      case 'blog':
        config = BLOG_CONTENT_CONFIG
        break
      case 'strict':
        config = STRICT_CONFIG
        break
      case 'default':
      default:
        config = DEFAULT_CONFIG
    }

    // Merge with custom options
    const finalConfig = {
      ...config,
      ...options
    }

    // Sanitize and return
    return DOMPurify.sanitize(html, finalConfig)
  }

  /**
   * Sanitize with default configuration
   */
  const sanitize = (html) => sanitizeHtml(html, {}, 'default')

  /**
   * Sanitize blog content (more permissive)
   */
  const sanitizeBlogContent = (html) => sanitizeHtml(html, {}, 'blog')

  /**
   * Sanitize user input (strict mode)
   */
  const sanitizeUserInput = (html) => sanitizeHtml(html, {}, 'strict')

  /**
   * Check if HTML contains potentially dangerous content
   * Returns true if sanitization would remove content
   *
   * @param {string} html - HTML to check
   * @returns {boolean} True if dangerous content detected
   */
  const containsDangerousContent = (html) => {
    if (!html) return false

    const sanitized = sanitizeHtml(html)
    // If sanitized version is significantly shorter, likely removed dangerous content
    return html.length - sanitized.length > 10
  }

  /**
   * Strip all HTML tags (return plain text only)
   *
   * @param {string} html - HTML to strip
   * @returns {string} Plain text without any HTML
   */
  const stripHtml = (html) => {
    if (!html) return ''

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [],  // No tags allowed
      KEEP_CONTENT: true  // Keep text content
    })
  }

  /**
   * Sanitize a URL to prevent javascript: / data: / vbscript: XSS injection
   * Only http: and https: protocols are allowed (LOW-001 fix)
   *
   * @param {string} url - URL to validate
   * @returns {string} Original URL if protocol is safe, '#' otherwise
   */
  const sanitizeUrl = (url) => {
    if (!url || typeof url !== 'string') return '#'
    try {
      const parsed = new URL(url)
      return (parsed.protocol === 'http:' || parsed.protocol === 'https:') ? url : '#'
    } catch {
      return '#'
    }
  }

  return {
    sanitizeHtml,
    sanitize,
    sanitizeBlogContent,
    sanitizeUserInput,
    containsDangerousContent,
    stripHtml,
    sanitizeUrl
  }
}

// Export configurations for testing purposes
export const configurations = {
  DEFAULT_CONFIG,
  BLOG_CONTENT_CONFIG,
  STRICT_CONFIG
}
